import {useRouter} from 'next/router';
import {useIntl} from 'react-intl';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Heading,
  Avatar,
  Flex,
  Text,
  Button,
  useDisclosure,
} from '@chakra-ui/react';

import {useTranslate} from '@eksab/i18n';
import {TShirtIcon} from '@eksab/assets/icons';
import {useLineupStore} from '@eksab/features/competition/lineup/useLineupStore';
import {getLanguage} from '@eksab/util/helpers';

import {InvalidReasonIndicator, NewPositionIndicator} from './';
import {OutOfLineupIcon, PositionChangedIcon} from './icons';
import type {InvalidPlayer, InvalidReason} from './types';

interface PlayerStatsModalProps {
  player: InvalidPlayer;
}

export const InvalidPlayerModal = ({player}: PlayerStatsModalProps) => {
  const {locale} = useRouter();
  const format = useIntl().formatNumber;
  const {isOpen, onClose} = useDisclosure({defaultIsOpen: true});

  const t = useTranslate();
  const openDrawer = useLineupStore((state) => state.openDrawer);
  const removePlayer = useLineupStore((state) => state.removePlayer);

  const onPlayerChange = () => {
    onClose();

    removePlayer(player, !!player.is_substitute);
    openDrawer();
  };

  const reason = player.selection_status?.status as InvalidReason;

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered motionPreset="slideInBottom" size="sm">
      <ModalOverlay />

      <ModalContent borderWidth="3px" borderColor="gray.900" bg="gray.900" rounded="xl" shadow="sm" overflow="hidden">
        <ModalHeader display="flex" gap="3" px="5" py="4">
          <Avatar
            className="player-avatar"
            bg="transparent"
            rounded="initial"
            boxSize="3.75rem"
            sx={{'> img': {bg: 'white'}}}
            src={player.image?.thumb ?? player.image?.original}
            icon={<TShirtIcon filled={false} width="full" height="auto" />}
          />

          <Flex
            className="player-basic-info"
            w="full"
            flexDir="column"
            color="white"
            fontWeight="700"
            overflow="hidden"
          >
            <Heading
              dir="auto"
              title={player.common_name}
              mb="2"
              size="xs"
              fontWeight="inherit"
              alignSelf="flex-start"
              color="inherit"
              maxW="85%"
              noOfLines={1}
            >
              {player.common_name}
            </Heading>

            <Text color="gray.500" fontWeight="inherit" fontSize="xs" maxW="90%" noOfLines={1}>
              {t(`fantasy.lineup.${player.position}`)} . {player.team.name}
            </Text>

            {reason === 'invalid-position' && (
              <NewPositionIndicator position={player.selection_status?.meta?.correct_position!} mt="2" />
            )}

            <Flex flex={1} gap="4" align="flex-end" justify="space-between">
              <Text color="white" fontSize="sm" fontWeight="inherit">
                {t('fantasy.lineup.price-unit', {amount: format(player.price)})}
              </Text>

              <InvalidReasonIndicator reason={reason} />
            </Flex>
          </Flex>
        </ModalHeader>
        <ModalCloseButton color="white" size="lg" top="1" insetEnd="1" />

        <ModalBody
          px="8"
          pt="5"
          pb="4"
          display="flex"
          flexDir="column"
          alignItems="center"
          gap="4"
          textAlign="center"
          bg="white"
        >
          {reason === 'invalid-player' ? (
            <OutOfLineupIcon width="10" height="auto" transform="auto" scaleX={getLanguage(locale) === 'en' ? 1 : -1} />
          ) : (
            <PositionChangedIcon
              width="10"
              height="auto"
              transform="auto"
              scaleX={getLanguage(locale) === 'en' ? 1 : -1}
            />
          )}

          <Heading as="h3" size="md" fontWeight="800" color="red.600">
            {t(`fantasy.lineup.invalid-player-warning.title.${reason}`)}
          </Heading>

          <Text fontSize="sm" fontWeight="700" color="gray.800">
            {t(`fantasy.lineup.invalid-player-warning.description.${reason}`)}
          </Text>
        </ModalBody>

        <ModalFooter bg="white" pt="0" pb="6">
          <Button w="full" tabIndex={1} onClick={onPlayerChange}>
            {t('fantasy.lineup.player.actions.change')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
